import {
  EventType,
  WebsocketEvent,
  WebsocketEventFilter,
} from '@upstart/harmony/lib/types/websocketTypes';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import { uniqueId } from 'lodash';

interface SubscribeProps {
  dealId: string;
  carId: string;
  dealRequest: (payload: { dealId: string }) => void;
  deskingRequest: (payload: { dealId: string; carId: string | null }) => void;
  dealFinancialsUpdateCallback: (payload: WebsocketEvent) => void;
}

export const subscribeToWebsockets = ({
  dealId,
  carId,
  dealRequest,
  deskingRequest,
  dealFinancialsUpdateCallback,
}: SubscribeProps) => {
  if (!dealId) {
    return;
  }
  WebsocketService.subscribe(
    {
      eventType: EventType.LOSS_EXPRESS_PAYOFF_UPDATED,
      dealId,
    } as WebsocketEventFilter,
    () => {
      dealRequest({ dealId });
    },
  );
  WebsocketService.subscribe(
    {
      eventType: EventType.DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED,
      dealId,
    } as WebsocketEventFilter,
    dealFinancialsUpdateCallback,
  );
};

export const unsubscribeFromWebsockets = (
  subscribedDealId: string | null,
  subscribedCarId: string | null,
) => {
  if (!subscribedDealId) {
    return;
  }
  WebsocketService.unsubscribe({
    eventType: EventType.DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED,
    dealId: subscribedDealId,
  } as WebsocketEventFilter);
  WebsocketService.unsubscribe({
    eventType: EventType.LOSS_EXPRESS_PAYOFF_UPDATED,
    dealId: subscribedDealId,
  } as WebsocketEventFilter);
};

// Helper function to allow subscribing to websocket events for a specific deal
// from different components without accidentally unsubscribing other components
const ricEventSignedListeners = new Map<string, () => void>();
export const subscribeToWebsocketRicSignedEvent = (
  dealId: string,
  handler: VoidFunction,
) => {
  // If there are currently no listeners yet, subscribe to the websocket event
  if (ricEventSignedListeners.size === 0) {
    const wsEvent = {
      dealId,
      eventType: EventType.DEAL_FINANCE_APPLICATION_RIC_SIGNED,
    };
    WebsocketService.subscribe(wsEvent, async () => {
      ricEventSignedListeners.forEach((listener) => listener());
    });
  }

  // Generate a random key to identify the listener
  const key = uniqueId();
  ricEventSignedListeners.set(key, handler);

  // Return a function to unsubscribe
  return () => {
    ricEventSignedListeners.delete(key);
    if (ricEventSignedListeners.size === 0) {
      WebsocketService.unsubscribe({
        eventType: EventType.DEAL_FINANCE_APPLICATION_RIC_SIGNED,
        dealId,
      });
    }
  };
};
